<script setup>
import { computed } from 'vue';
import { useStore } from 'vuex';
import { useMapGetter } from 'dashboard/composables/store';
import { useAlert } from 'dashboard/composables';
import { useI18n } from 'vue-i18n';
import wootConstants from 'dashboard/constants/globals';

import Button from 'dashboard/components-next/button/Button.vue';

const props = defineProps({
  message: {
    type: String,
    default: '',
  },
  isOnPrivateNote: {
    type: Boolean,
    default: false,
  },
});

const store = useStore();
const { t } = useI18n();

const currentChat = useMapGetter('getSelectedChat');
const currentUser = useMapGetter('getCurrentUser');

const assignedAgent = computed({
  get() {
    return currentChat.value?.meta?.assignee;
  },
  set(agent) {
    const agentId = agent ? agent.id : null;
    store.dispatch('setCurrentChatAssignee', {
      conversationId: currentChat.value?.id,
      assignee: agent,
      assigneeType: agent ? 'User' : null,
    });
    store.dispatch('assignAgent', {
      conversationId: currentChat.value?.id,
      agentId,
    });
  },
});

const hasMessage = computed(() => props.message !== '');
const isUserTyping = computed(() => hasMessage.value && !props.isOnPrivateNote);
const isUnassigned = computed(() => !assignedAgent.value);
const showSelfAssignBanner = computed(() => {
  return isUserTyping.value && isUnassigned.value;
});

const isPendingConversation = computed(
  () => currentChat.value?.status === wootConstants.STATUS_TYPE.PENDING
);

const isAgentBotOwned = computed(
  () => currentChat.value?.meta?.assignee_type === 'AgentBot'
);

const showBotHandoffBanner = computed(() => {
  return isPendingConversation.value && isAgentBotOwned.value;
});

const botAssigneeName = computed(() => {
  if (isAgentBotOwned.value && assignedAgent.value?.name) {
    return assignedAgent.value.name;
  }

  return t('CONVERSATION.BOT_HANDOFF_FALLBACK_ASSIGNEE');
});

const selfAssignConversation = async () => {
  const { avatar_url, ...rest } = currentUser.value || {};
  assignedAgent.value = { ...rest, thumbnail: avatar_url };
};

const needsAssignmentToCurrentUser = computed(() => {
  return isUnassigned.value;
});

const onClickSelfAssign = async () => {
  try {
    await selfAssignConversation();
    useAlert(t('CONVERSATION.CHANGE_AGENT'));
  } catch (error) {
    useAlert(t('CONVERSATION.CHANGE_AGENT_FAILED'));
  }
};

const reopenConversation = async () => {
  await store.dispatch('toggleStatus', {
    conversationId: currentChat.value?.id,
    status: 'open',
  });
};

const onClickBotHandoff = async () => {
  try {
    const shouldAssignToCurrentUser =
      isAgentBotOwned.value || needsAssignmentToCurrentUser.value;

    await reopenConversation();

    if (shouldAssignToCurrentUser) {
      await selfAssignConversation();
    }

    useAlert(t('CONVERSATION.BOT_HANDOFF_SUCCESS'));
  } catch (error) {
    useAlert(t('CONVERSATION.BOT_HANDOFF_ERROR'));
  }
};
</script>

<template>
  <div
    v-if="showSelfAssignBanner && !showBotHandoffBanner"
    class="reply-box-banner reply-box-banner--self-assign"
    role="status"
  >
    <span class="reply-box-banner__icon" aria-hidden="true">
      <span class="i-lucide-user-plus size-[1.125rem]" />
    </span>
    <div class="reply-box-banner__content">
      <p class="reply-box-banner__title">
        {{ $t('CONVERSATION.SELF_ASSIGN_TITLE') }}
      </p>
      <p class="reply-box-banner__message">
        {{ $t('CONVERSATION.NOT_ASSIGNED_TO_YOU') }}
      </p>
    </div>
    <Button
      color="ruby"
      size="sm"
      icon="i-lucide-user-check"
      :label="$t('CONVERSATION.ASSIGN_TO_ME')"
      class="reply-box-banner__action"
      @click="onClickSelfAssign"
    />
  </div>
  <div
    v-if="showBotHandoffBanner"
    class="reply-box-banner reply-box-banner--bot-handoff"
    role="status"
  >
    <span class="reply-box-banner__icon" aria-hidden="true">
      <span class="i-lucide-bot size-[1.125rem]" />
    </span>
    <div class="reply-box-banner__content">
      <p class="reply-box-banner__title">
        {{ botAssigneeName }}
      </p>
      <p class="reply-box-banner__message">
        {{
          $t('CONVERSATION.BOT_HANDOFF_MESSAGE', {
            assigneeName: botAssigneeName,
          })
        }}
      </p>
    </div>
    <Button
      color="amber"
      size="sm"
      icon="i-lucide-user-check"
      :label="$t('CONVERSATION.BOT_HANDOFF_ACTION')"
      class="reply-box-banner__action"
      @click="onClickBotHandoff"
    />
  </div>
</template>

<style scoped lang="scss">
.reply-box-banner {
  @apply mx-3 mb-2 mt-1 flex min-h-12 items-center gap-2.5 rounded-xl border px-3 py-1.5 shadow-sm;
}

.reply-box-banner--self-assign {
  @apply border-n-ruby-5/60 bg-n-ruby-2/60;
}

.reply-box-banner--bot-handoff {
  @apply border-n-amber-5/60 bg-n-amber-2/60;
}

.reply-box-banner__icon {
  @apply grid size-8 shrink-0 place-items-center rounded-lg;
}

.reply-box-banner--self-assign .reply-box-banner__icon {
  @apply bg-n-ruby-4 text-n-ruby-11;
}

.reply-box-banner--bot-handoff .reply-box-banner__icon {
  @apply bg-n-amber-4 text-n-amber-12;
}

.reply-box-banner__action {
  @apply shrink-0;
}

.reply-box-banner__content {
  @apply flex min-w-0 flex-1 items-baseline gap-2;
}

.reply-box-banner__title {
  @apply mb-0 shrink-0 text-sm font-semibold leading-5 text-n-slate-12;
}

.reply-box-banner__message {
  @apply mb-0 min-w-0 truncate text-xs leading-5 text-n-slate-10;
}

.reply-box-banner--bot-handoff .reply-box-banner__title {
  @apply text-n-amber-12;
}

.reply-box-banner--bot-handoff .reply-box-banner__message {
  @apply text-n-amber-11;
}

@media (max-width: 640px) {
  .reply-box-banner {
    @apply flex-wrap;
  }

  .reply-box-banner__action {
    @apply ms-12;
  }
}
</style>
